import React, {useState, useRef, useCallback} from 'react';
import ReactCrop, {Crop, PixelCrop, centerCrop, makeAspectCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slider,
  Typography,
  Stack,
  Paper,
  Switch,
  FormControlLabel,
  ButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Crop as CropIcon,
  AspectRatio as AspectRatioIcon,
  Straighten as StraightenIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface ImageCropProps {
  imageFile: File;
  onSave: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
  isOpen: boolean;
}

const ASPECT_RATIOS = [
  {label: 'Frei', value: undefined},
  {label: '1:1', value: 1},
  {label: '4:3', value: 4 / 3},
  {label: '16:9', value: 16 / 9},
  {label: '3:2', value: 3 / 2},
  {label: '2:3', value: 2 / 3},
];

export default function ImageCrop({imageFile, onSave, onCancel, isOpen}: ImageCropProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [keepOriginalSize, setKeepOriginalSize] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [imageFile]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (aspect) {
        const {width, height} = e.currentTarget;
        setCrop(
          centerCrop(makeAspectCrop({unit: '%', width: 90}, aspect, width, height), width, height)
        );
      } else {
        setCrop(
          centerCrop(
            {unit: '%', width: 90, height: 90},
            e.currentTarget.width,
            e.currentTarget.height
          )
        );
      }
    },
    [aspect]
  );

  const handleAspectChange = (newAspect: number | undefined) => {
    setAspect(newAspect);
    if (imgRef.current && newAspect) {
      const {width, height} = imgRef.current;
      setCrop(
        centerCrop(makeAspectCrop({unit: '%', width: 90}, newAspect, width, height), width, height)
      );
    }
  };

  const getCroppedImg = useCallback(
    async (image: HTMLImageElement, crop: PixelCrop, rotation = 0, scale = 1): Promise<Blob> => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const cropX = crop.x * scaleX;
      const cropY = crop.y * scaleY;
      const cropWidth = crop.width * scaleX;
      const cropHeight = crop.height * scaleY;

      const rotRad = (rotation * Math.PI) / 180;
      const {width: rotatedWidth, height: rotatedHeight} = rotateSize(
        cropWidth,
        cropHeight,
        rotation
      );

      if (keepOriginalSize) {
        canvas.width = cropWidth;
        canvas.height = cropHeight;
      } else {
        canvas.width = rotatedWidth;
        canvas.height = rotatedHeight;
      }

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      ctx.save();

      ctx.translate(centerX, centerY);
      ctx.rotate(rotRad);
      ctx.scale(scale, scale);

      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        -cropWidth / 2,
        -cropHeight / 2,
        cropWidth,
        cropHeight
      );

      ctx.restore();

      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            }
          },
          'image/png',
          1
        );
      });
    },
    [keepOriginalSize]
  );

  const rotateSize = (width: number, height: number, rotation: number) => {
    const rotRad = (Math.abs(rotation) * Math.PI) / 180;
    return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const handleSave = async () => {
    if (imgRef.current && completedCrop?.width && completedCrop?.height) {
      try {
        const croppedImageBlob = await getCroppedImg(
          imgRef.current,
          completedCrop,
          rotation,
          scale
        );
        onSave(croppedImageBlob);
      } catch (error) {
        console.error('Fehler beim Zuschneiden:', error);
      }
    }
  };

  const generatePreview = useCallback(async () => {
    if (
      !imgRef.current ||
      !previewCanvasRef.current ||
      !completedCrop?.width ||
      !completedCrop?.height
    ) {
      return;
    }

    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop, rotation, scale);
      const url = URL.createObjectURL(croppedBlob);

      const previewImg = new Image();
      previewImg.onload = () => {
        const canvas = previewCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const maxSize = 200;
        const ratio = Math.min(maxSize / previewImg.width, maxSize / previewImg.height);
        canvas.width = previewImg.width * ratio;
        canvas.height = previewImg.height * ratio;

        ctx.drawImage(previewImg, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
      };
      previewImg.src = url;
    } catch (error) {
      console.error('Fehler bei der Vorschau-Generierung:', error);
    }
  }, [completedCrop, rotation, scale, getCroppedImg]);

  React.useEffect(() => {
    generatePreview();
  }, [generatePreview]);

  const handleReset = () => {
    setRotation(0);
    setScale(1);
    setAspect(undefined);
    if (imgRef.current) {
      setCrop(
        centerCrop(
          {unit: '%', width: 90, height: 90},
          imgRef.current.width,
          imgRef.current.height
        )
      );
    }
  };

  const buttonStyle = {
    borderRadius: '16px',
    textTransform: 'none' as const,
    fontWeight: 600,
    padding: isMobile ? '12px 20px' : '10px 16px',
    fontSize: isMobile ? '16px' : '14px',
    minHeight: isMobile ? '48px' : '40px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
    },
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    color: 'white',
    border: 'none',
    '&:hover': {
      ...buttonStyle['&:hover'],
      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    },
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    color: 'white',
    border: 'none',
    '&:hover': {
      ...buttonStyle['&:hover'],
      background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
    },
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#374151',
    border: '2px solid #D1D5DB',
    '&:hover': {
      ...buttonStyle['&:hover'],
      background: 'rgba(255, 255, 255, 1)',
      borderColor: '#9CA3AF',
    },
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onCancel} 
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #E5E7EB',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            color: '#1F2937',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CropIcon />
          Bild zuschneiden
        </Typography>
        <IconButton 
          onClick={onCancel}
          sx={{
            ...secondaryButtonStyle,
            minHeight: '40px',
            width: '40px',
            padding: 0
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        
        {/* Crop Area - Center */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            position: 'relative',
          }}
        >
          {imageUrl && (
            <Box
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop: Crop) => setCrop(percentCrop)}
                onComplete={(c: PixelCrop) => setCompletedCrop(c)}
                aspect={aspect}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  alt="Zuschneiden"
                  src={imageUrl}
                  style={{
                    transform: `scale(${scale}) rotate(${rotation}deg)`,
                    maxWidth: '90vw',
                    maxHeight: '70vh',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </Box>
          )}
        </Box>

        {/* Side Panel für Controls (Desktop) */}
        {!isMobile && showControls && (
          <Box
            sx={{
              width: '350px',
              borderLeft: '1px solid #E5E7EB',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              p: 3,
              overflowY: 'auto',
            }}
          >
            <Stack spacing={3}>
              {/* Vorschau */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                  Vorschau
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 2,
                    background: 'rgba(249, 250, 251, 0.8)',
                    borderRadius: '12px',
                    border: '2px dashed #D1D5DB',
                  }}
                >
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                  />
                </Box>
              </Box>

              {/* Seitenverhältnis */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                  <AspectRatioIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Seitenverhältnis
                </Typography>
                <ButtonGroup orientation="vertical" variant="outlined" fullWidth size="small">
                  {ASPECT_RATIOS.map((ratio) => (
                    <Button
                      key={ratio.label}
                      onClick={() => handleAspectChange(ratio.value)}
                      variant={aspect === ratio.value ? 'contained' : 'outlined'}
                      sx={aspect === ratio.value ? primaryButtonStyle : secondaryButtonStyle}
                    >
                      {ratio.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>

              {/* Rotation */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                  <StraightenIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Rotation: {rotation}°
                </Typography>
                <Slider
                  value={rotation}
                  onChange={(_, value) => setRotation(value as number)}
                  min={-180}
                  max={180}
                  step={1}
                  sx={{
                    color: '#3B82F6',
                    '& .MuiSlider-thumb': {
                      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    },
                  }}
                />
              </Box>

              {/* Skalierung */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                  Skalierung: {Math.round(scale * 100)}%
                </Typography>
                <Slider
                  value={scale}
                  onChange={(_, value) => setScale(value as number)}
                  min={0.5}
                  max={3}
                  step={0.1}
                  sx={{
                    color: '#3B82F6',
                    '& .MuiSlider-thumb': {
                      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    },
                  }}
                />
              </Box>

              {/* Optionen */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={keepOriginalSize}
                      onChange={(e) => setKeepOriginalSize(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#3B82F6',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#3B82F6',
                        },
                      }}
                    />
                  }
                  label="Originalgröße beibehalten"
                  sx={{ color: '#374151' }}
                />
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Fixed Bottom Control Panel */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid #E5E7EB',
          p: isMobile ? 2 : 3,
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          boxShadow: '0 -10px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: isMobile ? 1 : 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '1200px',
            width: '100%',
          }}
        >
          {/* Action Buttons */}
          <Button
            onClick={handleReset}
            startIcon={<UndoIcon />}
            sx={{
              ...secondaryButtonStyle,
              minWidth: isMobile ? '120px' : '140px',
            }}
          >
            Zurücksetzen
          </Button>

          <Button
            onClick={() => setShowControls(!showControls)}
            startIcon={<CropIcon />}
            sx={{
              ...secondaryButtonStyle,
              minWidth: isMobile ? '120px' : '140px',
              display: isMobile ? 'none' : 'flex',
            }}
          >
            {showControls ? 'Tools ausblenden' : 'Tools anzeigen'}
          </Button>

          <Button
            onClick={onCancel}
            startIcon={<CancelIcon />}
            sx={{
              ...dangerButtonStyle,
              minWidth: isMobile ? '120px' : '140px',
              color: 'white !important',
            }}
          >
            Abbrechen
          </Button>

          <Button
            onClick={handleSave}
            startIcon={<SaveIcon />}
            disabled={!completedCrop?.width || !completedCrop?.height}
            sx={{
              ...primaryButtonStyle,
              minWidth: isMobile ? '120px' : '140px',
              opacity: (!completedCrop?.width || !completedCrop?.height) ? 0.5 : 1,
              color: 'white !important',
            }}
          >
            Übernehmen
          </Button>
        </Box>
      </Box>

      {/* Mobile Controls Overlay */}
      {isMobile && showControls && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(5px)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'flex-end',
          }}
          onClick={() => setShowControls(false)}
        >
          <Box
            sx={{
              width: '100%',
              background: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              p: 3,
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                Crop Einstellungen
              </Typography>
              
              {/* Mobile Controls Content hier einfügen */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Seitenverhältnis
                </Typography>
                <ButtonGroup orientation="vertical" variant="outlined" fullWidth>
                  {ASPECT_RATIOS.map((ratio) => (
                    <Button
                      key={ratio.label}
                      onClick={() => handleAspectChange(ratio.value)}
                      variant={aspect === ratio.value ? 'contained' : 'outlined'}
                    >
                      {ratio.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Stack>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
